import { currencyWrapper } from "@alphascriptsolutions/excel-core";
import { otherMarketDataWrapper } from "@alphascriptsolutions/excel-core";
import { sharePriceDataWrapper } from "@alphascriptsolutions/excel-core";
import { companyDataWrapper } from "@alphascriptsolutions/excel-core";
import { capitalizationActualsWrapper } from "@alphascriptsolutions/excel-core";
import { customBuiltIndexWrapper } from "@alphascriptsolutions/excel-core";
import { capitalizationSummaryWrapper } from "@alphascriptsolutions/excel-core";
import { marketDerivedDataWrapper } from "@alphascriptsolutions/excel-core";

/**
 * Retrieves share price data for a specific company on a given date via the AlphaScript API.
 * @customfunction
 * @param {string} symbol A unique company identifier code in the format "Exchange:Ticker" (e.g., "Nasdaq:AVGO").
 * @param {string} dataType The type of financial data to retrieve (e.g., "Closing_Share_Price").
 * @param {any} date The target date for the data, in MM/DD/YYYY format or Excel date.
 * @param {string} currency The desired output currency (e.g., "USD").
 * @param {CustomFunctions.Invocation} invocation The invocation object.
 * @requiresAddress
 */
export function share_price_data(
  symbol: string,
  dataType: string,
  date: any,
  currency: string,
  invocation: CustomFunctions.Invocation
): any {
  return sharePriceDataWrapper(symbol, dataType, date, currency, invocation);
}

/**
 * Retrieves the exchange rate for a currency pair via the AlphaScript API.
 * @customfunction
 * @param {string} symbol A currency pair in the format "Base:Quote" (e.g., "USD:EUR").
 * @param {any} date The target date for the data, in MM/DD/YYYY format or Excel date.
 * @param {string} dataType The type of data to retrieve. (e.g., "Daily_Closing_Value")
 * @param {CustomFunctions.Invocation} invocation The invocation object.
 * @requiresAddress
 */
export function currency(symbol: string, dataType: string, date: any, invocation: CustomFunctions.Invocation): any {
  return currencyWrapper(symbol, dataType, date, invocation);
}

/**
 * Retrieves other market data for a specific company on a given date via the AlphaScript API.
 * @customfunction
 * @param {string} symbol A unique company identifier code in the format "Exchange:Ticker" (e.g., "Nasdaq:AVGO").
 * @param {string} dataType The type of financial data to retrieve (e.g., "Traded_Volume_Shares").
 * @param {any} [date] The target date for the data, in MM/DD/YYYY format or Excel date. Only required for "Traded_Volume_Shares", "Adjusted_Traded_Volume_Shares", "Trading_Days" data types.
 * @param {any} [end_date] The target end date for the data, in MM/DD/YYYY format or Excel date. Only required for "Trading_Days" data type.
 * @param {CustomFunctions.Invocation} invocation The invocation object.
 * @returns {string[][]} A 2D array of strings representing the data.
 * @requiresAddress
 */
export function other_market_data(
  symbol: string,
  dataType: string,
  date: any,
  end_date: any,
  invocation: CustomFunctions.Invocation
): string[][] {
  return otherMarketDataWrapper(symbol, dataType, date, end_date, invocation);
}

/**
 * Retrieves company data for a specific company via the AlphaScript API.
 * @customfunction
 * @param {string} symbol A unique company identifier code in the format "Exchange:Ticker" (e.g., "Nasdaq:AVGO").
 * @param {string} dataType The type of financial data to retrieve (e.g., "Legal_Name").
 * @param {any} [currency] The desired output currency (e.g., "USD").
 * @param {CustomFunctions.Invocation} invocation The invocation object.
 * @requiresAddress
 */
export function company_data(
  symbol: string,
  dataType: string,
  currency: any,
  invocation: CustomFunctions.Invocation
): any {
  return companyDataWrapper(symbol, dataType, currency, invocation);
}

/**
 * Retrieves capitalization actuals for a specific company via the AlphaScript API.
 * @customfunction
 * @param {string} dataType The type of capitalization data to retrieve (e.g., "Data_Source_Information_Filing_Type").
 * @param {string[][]} dataSource The data sources (the access group, the UUN, the datasource)
 * @param {string} symbol A unique company identifier code in the format "Exchange:Ticker" (e.g., "Nasdaq:AVGO").
 * @param {any} date The target date for the data, in MM/DD/YYYY format or Excel date.
 * @param {CustomFunctions.Invocation} invocation The invocation object.
 * @requiresAddress
 */
export function capitalization_actuals(
  dataType: string,
  dataSource: string[][],
  symbol: string,
  date: any,
  invocation: CustomFunctions.Invocation
): any {
  return capitalizationActualsWrapper(dataType, dataSource, symbol, date, invocation);
}

/**
 * Builds a Custom Built Index using a standard Alpha Audit input range.
 *
 * The `inputs` range must match the structure of the "Custom_Index_Inputs_1" range
 * from the Alpha Audit template (contains: startDate, endDate, symbols, currency,
 * share price datatype, weighting method, and indexStartValue).
 *
 * @customfunction
 * @param {any[][]} inputs The Custom Built Index input range selected by the user (Alpha Audit structure).
 * @param {string} dataType The index datatype to retrieve (e.g., "closing_index_value").
 * @param {any} date The target date to return the index value (MM/DD/YYYY or Excel date). **Required**.
 * @param {CustomFunctions.Invocation} invocation The invocation object.
 * @requiresAddress
 */
export function custom_built_index(
  inputs: any[][],
  dataType: string,
  date: any,
  invocation: CustomFunctions.Invocation
): any {
  return customBuiltIndexWrapper(inputs, dataType, date, invocation);
}

/**
 * Retrieves capitalization summary data (e.g., Market Cap) for a specific company on a given date via the AlphaScript API.
 *
 * Usage example:
 * Alpha.Capitalization_Summary("NASDAQ:AAPL", "EODHD", "Market_Cap", "USD", "01/15/2021")
 *
 * @customfunction
 * @param {string} symbol A unique company identifier code in the format "Exchange:Ticker" (e.g., "NASDAQ:AAPL").
 * @param {string} source The data source. Only "EODHD" is supported.
 * @param {string} dataType The type of capitalization summary data to retrieve (e.g., "Market_Cap").
 * @param {string} currency The desired output currency (e.g., "USD").
 * @param {any} date The target date for the data, in MM/DD/YYYY format or Excel date.
 * @param {CustomFunctions.Invocation} invocation The invocation object.
 * @requiresAddress
 */
export function capitalization_summary(
  symbol: string,
  source: string,
  dataType: string,
  currency: string,
  date: any,
  invocation: CustomFunctions.Invocation
): any {
  return capitalizationSummaryWrapper(symbol, source, dataType, currency, date, invocation);
}

/**
 * Computes a single derived market metric over a date range (Start → End) using EODHD daily data.
 *
 * Usage example:
 * Alpha.Market_Derived_Data("NASDAQ:AAPL", "Period_VWAP", "01/01/2025", "01/31/2025", "USD")
 *
 * Supported Data Types:
 * - "Period_High"
 * - "Period_Low"
 * - "Period_VWAP"
 * - "Period_Average"
 * - "Period_Median"
 * - "Period_P25"
 * - "Period_P75"
 *
 * Share Price Type rules (current strict policy):
 * - Period_High => Daily_High / Adjusted_Daily_High
 * - Period_Low  => Daily_Low / Adjusted_Daily_Low
 * - Others      => Closing/Opening (adjusted or not)
 *
 * @customfunction
 * @param {string} symbol A unique company identifier code in the format "Exchange:Ticker" (e.g., "NASDAQ:AAPL").
 * @param {string} dataType The derived datatype to retrieve (e.g., "Period_VWAP").
 * @param {any} start_date Start date (MM/DD/YYYY or Excel date).
 * @param {any} end_date End date (MM/DD/YYYY or Excel date).
 * @param {string} currency Desired output currency (e.g., "USD" or "TRADING_CURRENCY").
 * @param {CustomFunctions.Invocation} invocation The invocation object.
 * @requiresAddress
 */
export function market_derived_data(
  symbol: string,
  dataType: string,
  start_date: any,
  end_date: any,
  currency: string,
  invocation: CustomFunctions.Invocation
): any {
  return marketDerivedDataWrapper(symbol, dataType, start_date, end_date, currency, invocation);
}

CustomFunctions.associate("SHARE_PRICE_DATA", share_price_data);
CustomFunctions.associate("CURRENCY", currency);
CustomFunctions.associate("OTHER_MARKET_DATA", other_market_data);
CustomFunctions.associate("COMPANY_DATA", company_data);
CustomFunctions.associate("CAPITALIZATION_ACTUALS", capitalization_actuals);
CustomFunctions.associate("CUSTOM_BUILT_INDEX", custom_built_index);
CustomFunctions.associate("CAPITALIZATION_SUMMARY", capitalization_summary);
CustomFunctions.associate("MARKET_DERIVED_DATA", market_derived_data);